import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

/*
 * HSE's national batch list, plus this practice's own private batches (KIN-1679/KIN-1740).
 *
 * `inDateOn` is the day the batches must still be in date on, and it is the vaccination's
 * date given rather than today: a dose recorded a fortnight after it was administered
 * must offer the batches that were in date then, not the ones in date now. The endpoint
 * defaults it to today when it is omitted, which is the wrong answer for a backdated
 * dose, so it is always sent.
 *
 * Expired batches are left out rather than listed and marked. The server refuses nothing
 * on expiry alone, but the form's own cross-field rule already rejects a dose given after
 * its vaccine expired, so an expired batch is an option that cannot be saved. The one
 * expired batch a clinician may still need to see — the one an existing record was given
 * from — is re-offered from the record itself (`toRecordedBatchOption`), not fetched.
 *
 * Darren: "this list rarely changes", and it is a national list of a few hundred rows, so
 * it is held for a good while rather than refetched on every focus.
 */
const BATCH_LIST_STALE_TIME_MS = 15 * 60 * 1000;

export const useVaccineBatches = ({ inDateOn = null, enabled = true } = {}) => {
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["vaccineBatches", tenantVal, inDateOn],
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(`${tenantVal}/vaccine-batches`, {
        params: inDateOn ? { inDateOn } : {},
      });

      // The interceptor unwraps the ApiResponse envelope, so this is the plain array. An
      // empty list is a valid answer, not a failure.
      return Array.isArray(response?.data) ? response.data : [];
    },
    enabled: enabled && !!tenantVal,
    staleTime: BATCH_LIST_STALE_TIME_MS,
    meta: { errorMessage: "Error fetching vaccine batches" },
  });
};
